/**
 * v1.2b — eSIM Provisioning Wizard MCP App
 *
 * 3-step wizard: select subscriber → select package → confirm + execute.
 * Tier-gated: pro and enterprise only (free tier is skipped).
 * Step 2→3 fetches package template details for preview; step 3 (confirm) calls affectPackageToSubscriber.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import {
  registerAppTool,
  registerAppResource,
  RESOURCE_MIME_TYPE,
} from "@modelcontextprotocol/ext-apps/server";
import { OcsClient } from "../client.js";
import { getDefaultResellerId, type ToolContext } from "../tools.js";
import {
  loadWizardSession,
  saveWizardSession,
  deleteWizardSession,
} from "./app-state.js";

// ── Helpers ───────────────────────────────────────────────────────────────────

async function safeCallWithToken<T = Record<string, unknown>>(
  client: OcsClient,
  _token: string,
  method: string,
  params: Record<string, unknown> | number | string = {},
): Promise<{ data: T | null; error: string | null }> {
  try {
    return { data: await client.call<T>(method, params), error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

function generateWizardId(): string {
  const arr = new Uint8Array(4);
  crypto.getRandomValues(arr);
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ── Registration ──────────────────────────────────────────────────────────────

export function registerProvisioningWizard(
  server: McpServer,
  ctx: ToolContext,
): void {
  // Tier guard — free tier does not get this wizard
  if (ctx.props.tier === "free") return;

  // ── Resource ───────────────────────────────────────────────────────────────
  registerAppResource(
    server,
    "eSIM Provisioning Wizard",
    "ui://esim-provisioning-wizard",
    {
      description:
        "3-step eSIM provisioning wizard — pick subscriber, pick package, confirm + execute.",
    },
    async () => {
      let html: string;
      try {
        const resp = await ctx.env.ASSETS.fetch(
          new Request(
            "https://internal/views/provisioning-wizard/index.html",
          ),
        );
        html = await resp.text();
      } catch {
        html = "<html><body><p>Provisioning wizard unavailable.</p></body></html>";
      }
      return {
        contents: [
          {
            uri: "ui://esim-provisioning-wizard",
            mimeType: RESOURCE_MIME_TYPE,
            text: html,
            _meta: { ui: {} },
          },
        ],
      };
    },
  );

  // ── Tool ───────────────────────────────────────────────────────────────────
  registerAppTool(
    server,
    "provision_esim_wizard",
    {
      title: "eSIM Provisioning Wizard",
      description:
        "Interactive 3-step wizard to provision an eSIM: select subscriber, choose package template, preview (dry_run) and confirm execution.",
      inputSchema: {
        step: z
          .enum(["init", "select-package", "preview", "confirm"])
          .describe("Current wizard step"),
        wizardId: z
          .string()
          .optional()
          .describe("Wizard session ID (absent on init)"),
        subscriber_iccid: z
          .string()
          .optional()
          .describe("Subscriber ICCID (required for select-package)"),
        package_template_id: z
          .number()
          .optional()
          .describe("Package template ID (required for preview)"),
      },
      _meta: {
        ui: {
          resourceUri: "ui://esim-provisioning-wizard",
          visibility: ["model", "app"] as ["model", "app"],
        },
      },
    },
    async ({ step, wizardId, subscriber_iccid, package_template_id }) => {
      const token = await ctx.getUserToken(ctx.props.sub);
      const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);

      // ── init ────────────────────────────────────────────────────────────────
      if (step === "init") {
        const newWizardId = generateWizardId();
        const subscribersResult = await safeCallWithToken<unknown[]>(
          client,
          token,
          "listResellerAccount",
          {},
        );

        const session = {
          step: "select-subscriber" as const,
          dry_run: false,
          initiated_at: new Date().toISOString(),
        };
        await saveWizardSession(
          ctx.env,
          ctx.props.sub,
          newWizardId,
          session,
        );

        return {
          content: [
            {
              type: "text" as const,
              text: `Wizard started (id: ${newWizardId}). Select a subscriber to provision.`,
            },
          ],
          structuredContent: {
            wizardId: newWizardId,
            step: "select-subscriber",
            subscribers: subscribersResult.data ?? [],
            error: subscribersResult.error,
          },
        };
      }

      // All subsequent steps require a wizardId
      if (!wizardId) {
        return {
          content: [{ type: "text" as const, text: "Missing wizardId." }],
          isError: true,
        };
      }

      // ── select-package ──────────────────────────────────────────────────────
      if (step === "select-package") {
        const session = await loadWizardSession(
          ctx.env,
          ctx.props.sub,
          wizardId,
        );
        if (!session) {
          return {
            content: [
              {
                type: "text" as const,
                text: "Wizard session not found or expired.",
              },
            ],
            isError: true,
          };
        }
        if (session.step !== "select-subscriber") {
          return {
            content: [
              {
                type: "text" as const,
                text: `Invalid step transition: expected select-subscriber, got ${session.step}.`,
              },
            ],
            isError: true,
          };
        }
        if (!subscriber_iccid) {
          return {
            content: [
              {
                type: "text" as const,
                text: "subscriber_iccid is required for select-package step.",
              },
            ],
            isError: true,
          };
        }

        // OCS method is listPrepaidPackageTemplate; requires resellerId param.
        const resellerIdForTemplates = await getDefaultResellerId(ctx.env, token);
        const packagesResult = await safeCallWithToken<unknown[]>(
          client,
          token,
          "listPrepaidPackageTemplate",
          { resellerId: resellerIdForTemplates },
        );

        const updated = {
          ...session,
          step: "select-package" as const,
          subscriber_iccid,
        };
        await saveWizardSession(ctx.env, ctx.props.sub, wizardId, updated);

        return {
          content: [
            {
              type: "text" as const,
              text: `Subscriber ${subscriber_iccid} selected. Choose a package template.`,
            },
          ],
          structuredContent: {
            wizardId,
            step: "select-package",
            subscriber_iccid,
            packages: packagesResult.data ?? [],
            error: packagesResult.error,
          },
        };
      }

      // ── preview ─────────────────────────────────────────────────────────────
      if (step === "preview") {
        const session = await loadWizardSession(
          ctx.env,
          ctx.props.sub,
          wizardId,
        );
        if (!session) {
          return {
            content: [
              {
                type: "text" as const,
                text: "Wizard session not found or expired.",
              },
            ],
            isError: true,
          };
        }
        if (session.step !== "select-package") {
          return {
            content: [
              {
                type: "text" as const,
                text: `Invalid step transition: expected select-package, got ${session.step}.`,
              },
            ],
            isError: true,
          };
        }
        if (package_template_id === undefined) {
          return {
            content: [
              {
                type: "text" as const,
                text: "package_template_id is required for preview step.",
              },
            ],
            isError: true,
          };
        }

        // Fix #19: modifyAccountPackage does not exist in OCS v1.
        // Preview step fetches the package template details (read-only) to show
        // the operator what will be assigned before confirm executes the real call.
        const previewResult = await safeCallWithToken<unknown[]>(
          client,
          token,
          "listPrepaidPackageTemplate",
          {},
        );

        const list = previewResult.data;
        let preview: unknown = null;
        if (Array.isArray(list)) {
          preview =
            list.find((item) => {
              if (!item || typeof item !== "object") return false;
              const rec = item as Record<string, unknown>;
              const tid = rec.templateId ?? rec.packageTemplateId;
              return Number(tid) === package_template_id;
            }) ?? null;
        } else if (list && typeof list === "object") {
          const rec = list as Record<string, unknown>;
          const tid = rec.templateId ?? rec.packageTemplateId;
          if (Number(tid) === package_template_id) preview = list;
        }

        const updated = {
          ...session,
          step: "confirm" as const,
          package_template_id,
          dry_run: true,
        };
        await saveWizardSession(ctx.env, ctx.props.sub, wizardId, updated);

        return {
          content: [
            {
              type: "text" as const,
              text: `Preview ready. Review changes and confirm to proceed.`,
            },
          ],
          structuredContent: {
            wizardId,
            step: "confirm",
            subscriber_iccid: session.subscriber_iccid,
            package_template_id,
            preview,
            previewError: previewResult.error,
          },
        };
      }

      // ── confirm ─────────────────────────────────────────────────────────────
      if (step === "confirm") {
        const session = await loadWizardSession(
          ctx.env,
          ctx.props.sub,
          wizardId,
        );
        if (!session) {
          return {
            content: [
              {
                type: "text" as const,
                text: "Wizard session not found or expired.",
              },
            ],
            isError: true,
          };
        }
        if (session.step !== "confirm") {
          return {
            content: [
              {
                type: "text" as const,
                text: `Invalid step transition: expected confirm, got ${session.step}.`,
              },
            ],
            isError: true,
          };
        }

        // Fix #19: modifyAccountPackage does not exist in OCS v1. The correct
        // method is affectPackageToSubscriber.
        //
        // It wants a SubscriberId OBJECT `{ iccid }`, not a bare integer — live
        // OCS rejects the integer with "Cannot construct instance of
        // SubscriberId ... no int-argument constructor". This mirrors the
        // assign_package handler in ../tools.ts, which already sends the object
        // form. The getSingleSubscriber round-trip that only existed to produce
        // that integer is therefore gone; the ICCID goes straight through.
        const result = await safeCallWithToken(
          client,
          token,
          "affectPackageToSubscriber",
          {
            subscriber: { iccid: session.subscriber_iccid },
            packageTemplateId: session.package_template_id,
          },
        );

        await deleteWizardSession(ctx.env, ctx.props.sub, wizardId);

        if (result.error) {
          return {
            content: [
              {
                type: "text" as const,
                text: `Provisioning failed: ${result.error}`,
              },
            ],
            isError: true,
          };
        }

        return {
          content: [{ type: "text" as const, text: "Provisioned." }],
          structuredContent: {
            wizardId,
            step: "done",
            result: result.data,
          },
        };
      }

      return {
        content: [{ type: "text" as const, text: `Unknown step: ${step as string}.` }],
        isError: true,
      };
    },
  );
}
